import { Controller } from 'stimulus'

/**
 * Manages js for the home page Goldilocks (StimulusJS controller)
 *
 * @class GoldyController
 * @memberof Controllers
 */
export default class extends Controller {
  static targets = [
    'formType',
    'purchaseStep',
    'refinanceStep',
    'back',
    'next',
    'endorser',
    'originalEndorser',
  ]

  /**
   * @function Controllers.GoldyController.back
   * @memberof Controllers
   * @description Public interface to _stepUI, activates previous step
   *
   * @param {Event} event - User initiated click event.
   *
   * @return {undefined} manipulates DOM
   *
   */
  back(event) {
    event.preventDefault()

    this._stepUI('back')
  }

  /**
   * @function Controllers.GoldyController.next
   * @memberof Controllers
   * @description Public interface to _stepUI, activates next step
   *
   * @param {Event} event - User initiated click event.
   *
   * @return {undefined} manipulates DOM
   *
   */
  next(event) {
    event.preventDefault()

    const valid = this._stepValid()
    if (valid) {
      this._stepUI('forward')
    }
  }

  /**
   * @function Controllers.GoldyController.setFormType
   * @memberof Controllers
   * @description Sets the form type when the user chooses a form. This in turn
   * causes the machinery to hide/show the appropriate set of form steps.
   *
   * @param {Event} event - User initiated click event.
   *
   * @return {undefined} manipulates DOM
   *
   */
  setFormType(event) {
    event.preventDefault()
    const formType = event.target.dataset.formTypeValue

    this.formTypeTarget.value = formType
    this.data.set('formType', formType)
    this._setEndorser(formType)

    this.next(event)
  }

  /**
   * @function Controllers.GoldyController.validateAndSubmit
   * @memberof Controllers
   * @description Triggers validation one last time and if valid submits the
   * form using the traditional button submit mechanism
   *
   * @param {Event} event - User initiated click event.
   *
   * @return {undefined} manipulates DOM
   *
   */
  validateAndSubmit(event) {
    if (this._stepValid()) {
      return true
    } else {
      event.preventDefault()
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }


  // Private


  /**
   * @function Controllers.GoldyController._setEndorser
   * @memberof Controllers
   * @description Potentially sets endorser/original endorser if the values
   * haven't already been set by the maybe-form-marketing-values
   *
   * @param {String} formType - String representing user chosen form type
   * @private
   *
   * @return {undefined} manipulates DOM
   *
   */
  _setEndorser(formType) {
    const endorser = `Short ${formType.charAt(0).toUpperCase() + formType.slice(1)}`

    this.endorserTarget.value = endorser
    this.originalEndorserTarget.value = endorser
  }

  /**
   * @function Controllers.GoldyController._stepUI
   * @memberof Controllers
   * @description Activates next or previous step by toggling visibility for a
   * given set of form type steps
   * @param {String} direction - direction of form traversal
   * @private
   *
   * @return {undefined} manipulates DOM
   *
   */
  _stepUI(direction) {
    const steps = this[`${this.formType}StepTargets`]

    steps[this.stepNum].classList.toggle('goldy__card--hidden')
    if (direction === 'forward') {
      this.stepNum ++
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } else if (direction === 'back') {
      this.stepNum --
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
    steps[this.stepNum].classList.toggle('goldy__card--hidden')
  }

  /**
   * @function Controllers.GoldyController._stepValid
   * @memberof Controllers
   * @description Invokes the required-field controller to acheive individual
   * step validation. Note: This validation occurs only on non display: none
   * elements
   * @private
   *
   * @return {undefined} manipulates DOM
   *
   */
  _stepValid() {
    const validator = this.application.getControllerForElementAndIdentifier(this.element, 'required-field')
    return validator.validate()
  }

  /**
   * @function Controllers.GoldyController.stepNum
   * @memberof Controllers
   * @description Getter for stepNum
   * @private
   *
   * @return {Integer} stepNum - The integer representation of stepNum
   *
   */
  get stepNum() {
    return parseInt(this.data.get('stepNum'))
  }

  /**
   * @function Controllers.GoldyController.stepNum
   * @memberof Controllers
   * @description Setter for stepNum
   * @private
   *
   * @param {Integer} value - Manipulated Interger value corresponding to
   * stepNum
   *
   * @return {undefined} manipulates DOM
   *
   */
  set stepNum(value) {
    this.data.set('stepNum', value)
  }

  /**
   * @function Controllers.GoldyController.formType
   * @memberof Controllers
   * @description Getter for stepNum
   * @private
   *
   * @return {String} formType - the data attribute corresponding to formType
   *
   */
  get formType() {
    return this.data.get('formType')
  }
}

